import { Injectable, Injector, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { Store } from '@ngrx/store';

import { environment } from '../../environments/environment';
import { setAuthToken } from '../../app/state/actions';

/** The result of asking MSAL for a usable access token. */
export type TokenRefreshOutcome =
  | { status: 'refreshed'; accessToken: string }
  | { status: 'interaction-required' }
  | { status: 'no-account' }
  | { status: 'failed'; error: unknown };

/**
 * Gets the current access token from MSAL, renewing it silently when it has aged out.
 *
 * MSAL has held a refresh token since login; the app simply never redeemed it, and sent
 * a copy of the original access token until it lapsed. Asking MSAL per request instead
 * means the token in flight is always the current one, and the renewal happens against
 * the refresh token without the operator seeing anything.
 *
 * Azure AD caps a SPA refresh token at 24 hours from the last interactive sign-in and
 * does not extend that window when the token is redeemed, so silent renewal eventually
 * runs out. That case surfaces as `interaction-required`, which is the only outcome that
 * should reach the re-authorization dialog.
 *
 * This service deliberately knows nothing about the dialog. Callers decide what a failed
 * renewal means for them, which keeps the dependency pointing one way.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {

  /**
   * In-flight acquisitions, so a screen firing several requests at the moment of expiry
   * triggers one renewal rather than one per request. Forced and cached acquisitions are
   * tracked separately because they are not interchangeable.
   */
  private readonly inFlight = new Map<'cached' | 'forced', Promise<TokenRefreshOutcome>>();

  private readonly injector = inject(Injector);
  private msal?: MsalService;

  constructor(private store: Store) { }

  /**
   * Resolved on first use rather than injected.
   *
   * This service sits under AppUtilityService, which most component specs pull in for
   * unrelated reasons. Injecting MsalService in the constructor would oblige every one of
   * them to provide an auth mock for a code path they never exercise. Resolving it here
   * still fails loudly if it is genuinely missing when a token is needed.
   */
  private get authService(): MsalService {
    return this.msal ??= this.injector.get(MsalService);
  }

  /**
   * Returns a usable access token, renewing it if needed.
   *
   * @param forceRefresh Skip MSAL's cached access token and go straight to the refresh
   *   token. Used for a scheduled renewal ahead of expiry, and to retry a request the
   *   server has already rejected with a 401.
   */
  acquire(forceRefresh = false): Promise<TokenRefreshOutcome> {
    const key = forceRefresh ? 'forced' : 'cached';

    const pending = this.inFlight.get(key);
    if (pending) {
      return pending;
    }

    const attempt = this.acquireSilent(forceRefresh)
      .finally(() => this.inFlight.delete(key));

    this.inFlight.set(key, attempt);
    return attempt;
  }

  /** The most recent token the app stored, used only as a fallback when MSAL is unreachable. */
  lastKnownToken(): string | null {
    return localStorage.getItem('token');
  }

  private async acquireSilent(forceRefresh: boolean): Promise<TokenRefreshOutcome> {
    const account = this.activeAccount();
    if (!account) {
      return { status: 'no-account' };
    }

    try {
      // Idempotent, and cheap once done. Guards against a request that beats
      // MsalRedirectComponent's own initialization at startup.
      await this.authService.instance.initialize();

      const result = await this.authService.instance.acquireTokenSilent({
        scopes: [...environment.apiConfig.scopes],
        account,
        forceRefresh
      });

      this.publish(result.accessToken);
      return { status: 'refreshed', accessToken: result.accessToken };
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return { status: 'interaction-required' };
      }
      return { status: 'failed', error };
    }
  }

  /** The account the operator is signed in as, if any. */
  private activeAccount(): AccountInfo | null {
    return this.authService.instance.getActiveAccount()
      ?? this.authService.instance.getAllAccounts()[0]
      ?? null;
  }

  /**
   * Publishes a renewed token to the places the rest of the app still reads it from.
   * Only on a change, so a cache hit does not churn the store on every request.
   */
  private publish(accessToken: string): void {
    if (localStorage.getItem('token') === accessToken) {
      return;
    }
    localStorage.setItem('token', accessToken);
    this.store.dispatch(setAuthToken({ authToken: accessToken }));
  }
}
